import {useEffect, useState} from 'react';
import type {UploadItem} from '../api/files';

interface Props {
  items: UploadItem[];
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
}

export function UploadQueue({items, onCancel, onRetry}: Props) {
  const hasActive = items.some((i) => i.status === 'uploading');
  const now = useNow(hasActive);

  if (items.length === 0) return null;

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <UploadCard
          key={item.id}
          item={item}
          now={now}
          onCancel={onCancel}
          onRetry={onRetry}
        />
      ))}
    </ul>
  );
}

// Один спільний таймер для всієї черги — пересмикує now раз на секунду тільки
// поки є хоч один айтем у `uploading`. Картки читають це значення і обчислюють
// власну тривалість самі — без своїх інтервалів і зайвих re-render-ів сусідів.
function useNow(active: boolean): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

function UploadCard({
  item,
  now,
  onCancel,
  onRetry,
}: {
  item: UploadItem;
  now: number;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  const percent =
    item.status === 'done'
      ? 100
      : item.status === 'queued'
        ? 0
        : item.total > 0
          ? Math.min(100, Math.round((item.loaded / item.total) * 100))
          : 0;

  const isWaitingNetwork = item.status === 'uploading' && !item.online;

  const barClass =
    item.status === 'error'
      ? 'bg-red-500'
      : item.status === 'done'
        ? 'bg-emerald-500'
        : item.status === 'queued'
          ? 'bg-slate-300'
          : isWaitingNetwork
            ? 'bg-slate-400'
            : 'bg-sky-500';

  const sizeLabel =
    item.status === 'uploading' || item.status === 'error'
      ? formatProgress(item.loaded, item.total)
      : formatBytes(item.total);

  const durationMs = computeDuration(item, now);
  const durationLabel = durationMs !== null ? formatDuration(durationMs) : null;

  const statusLabel =
    item.status === 'done'
      ? `${sizeLabel} ✓${durationLabel ? ` · ${durationLabel}` : ''}`
      : item.status === 'error'
        ? `${sizeLabel} !`
        : item.status === 'queued'
          ? `${sizeLabel} · в очереди`
          : isWaitingNetwork
            ? `${sizeLabel} · ожидание сети…`
            : `${sizeLabel} · ${percent}%${durationLabel ? ` · ${durationLabel}` : ''}`;

  const canCancel = item.status !== 'done';

  return (
    <li className="border border-slate-200 rounded-2xl p-4 bg-white">
      <div className="flex items-center justify-between gap-4 mb-2">
        <p className="break-all font-medium text-slate-900 min-w-0 flex-1">
          {item.filename}
        </p>
        <span className="shrink-0 text-sm text-slate-500 tabular-nums">
          {statusLabel}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {canCancel ? (
          <button
            type="button"
            onClick={() => onCancel(item.id)}
            className="shrink-0 text-slate-400 hover:text-red-500 text-sm leading-none"
            aria-label="Отменить"
            title="Отменить"
          >
            ✕
          </button>
        ) : (
          <span aria-hidden className="shrink-0 w-3" />
        )}
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex-1">
          <div
            className={`h-full transition-[width] duration-150 ${barClass}`}
            style={{width: item.status === 'queued' ? '100%' : `${percent}%`}}
          />
        </div>
      </div>
      {item.status === 'error' && (
        <div className="mt-2 flex items-start justify-between gap-3">
          {item.error && (
            <p className="text-sm text-red-600 min-w-0 flex-1">
              {describeUploadError(item.error)}
            </p>
          )}
          <button
            type="button"
            onClick={() => onRetry(item.id)}
            className="shrink-0 text-sm font-medium text-sky-600 hover:text-sky-700"
          >
            Продолжить
          </button>
        </div>
      )}
    </li>
  );
}

// Скільки тривала ця спроба. Для `uploading` — від startedAt до now (живе число,
// тікає кожну секунду). Для `done`/`error` — від startedAt до finishedAt
// (заморожене). Якщо startedAt ще не виставлений (айтем у `queued` і чекає
// слот) — null, і таймер не показуємо.
function computeDuration(item: UploadItem, now: number): number | null {
  if (item.startedAt === undefined) return null;
  if (item.status === 'uploading') return Math.max(0, now - item.startedAt);
  if (item.finishedAt !== undefined) return Math.max(0, item.finishedAt - item.startedAt);
  return null;
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}с`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return `${min}м ${sec}с`;
  const hours = Math.floor(min / 60);
  const minMod = min % 60;
  return `${hours}ч ${minMod}м`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

// Один юніт по обох сторонах — «4.5 / 9.2 МБ» читається легше за «4500 КБ / 9.2 МБ».
function formatProgress(loaded: number, total: number): string {
  const MB = 1024 * 1024;
  const GB = MB * 1024;
  if (total >= GB) {
    return `${(loaded / GB).toFixed(2)} / ${(total / GB).toFixed(2)} ГБ`;
  }
  if (total >= MB) {
    return `${(loaded / MB).toFixed(1)} / ${(total / MB).toFixed(1)} МБ`;
  }
  return `${Math.round(loaded / 1024)} / ${Math.round(total / 1024)} КБ`;
}

// Маппимо коди помилок з бекенду на людські повідомлення. Все що не
// розпізнане — показуємо як є (це або новий код, який треба додати, або
// якесь повідомлення з мережевого шару).
function describeUploadError(code: string): string {
  switch (code) {
    case 'network_error':
      return 'Не удалось соединиться с сервером. Попробуйте ещё раз.';
    case 'timeout':
      return 'Соединение слишком долго не отвечало. Попробуйте ещё раз.';
    case 'upload_stalled':
      return 'Загрузка остановилась — 30 минут без прогресса. Нажмите «Продолжить».';
    case 'anketa_folder_not_found':
      return 'Папка для этой анкеты не найдена. Запросите ссылку у бота заново.';
    case 'no_file':
      return 'Файл пустой или повреждён.';
    case 'file_too_large':
      return 'Файл слишком большой.';
    case 'not_uploading_state':
      return 'Этот заказ уже передан, загрузка закрыта.';
    case 'forbidden':
    case 'token_expired':
      return 'Ссылка просрочена. Запросите новую у бота.';
    case 'endpoint_removed':
      return 'Версия страницы устарела. Обновите страницу (потяните вниз) и попробуйте снова.';
    case 'internal_error':
    case 'http_error':
      return 'Ошибка на сервере. Попробуйте ещё раз через минуту.';
    default:
      return `Ошибка: ${code}`;
  }
}
